/**
 * Site-wide scroll reveal.
 *
 * Safety first: the CSS that hides elements is gated behind the `dom-reveal-on`
 * class, which is only added here. If this file fails to parse or never runs,
 * nothing is ever hidden and the page reads exactly as it would without it.
 *
 * Deliberately NOT using IntersectionObserver. IO silently never fires in some
 * embedded webviews, and TikTok / Instagram in-app browsers are where most of
 * this store's traffic lands. A geometry check over a handful of nodes is
 * cheaper than a bug nobody can reproduce on a desktop.
 *
 * Targets are chosen here rather than marked up in Liquid. Hand-tagging meant
 * adding an attribute to every section of every template, which is how this
 * shipped inert the first time - the stylesheet and this script were loading on
 * every page while not a single element carried the attribute.
 */
(() => {
  const root = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  if (reduced.matches) return; // leave everything visible, do nothing

  root.classList.add('dom-reveal-on');

  const SHOW_AT = 0.92; // reveal once the top edge is within 92% of the viewport
  const STAGGER_MS = 70;
  const STAGGER_CAP = 6; // past this the wait is just dead time
  let nodes = [];

  /**
   * Picks what animates.
   *
   * A grid gets its cards tagged so they arrive one after another; anything
   * else gets its direct children, which is the level where a heading, a
   * paragraph and a button read as separate beats.
   *
   * The hero is skipped outright: it runs its own crossfade and camera move,
   * and it sits at the top of the page, so fading it in would fight the one
   * animation on the site that is already deliberate.
   */
  const tag = () => {
    document.querySelectorAll('main > .shopify-section, footer > .shopify-section').forEach((section) => {
      if (section.querySelector('.dom-hero')) return;

      const cards = section.querySelectorAll('.product-card, .resource-list__item');
      const targets = cards.length ? cards : section.querySelectorAll(':scope > * > *');

      targets.forEach((el, i) => {
        if (el.hasAttribute('data-dom-reveal')) return;
        if (el.closest('.dom-hero')) return;
        el.setAttribute('data-dom-reveal', '');
        // Stagger restarts per section, so a grid halfway down the page does not
        // inherit a two-second delay from the number of things above it.
        el.style.setProperty('--dom-reveal-delay', `${Math.min(i, STAGGER_CAP) * STAGGER_MS}ms`);
      });
    });
  };

  const collect = () => {
    nodes = Array.prototype.slice.call(document.querySelectorAll('[data-dom-reveal]'));
  };

  /**
   * IntersectionObserver where it exists, geometry where it does not.
   *
   * The geometry path alone could not be made correct. It has to ask "is this
   * on screen" at some moment, and every moment early enough to matter is one
   * where the lazy product images have reserved no height - so the cards are
   * still bunched near the top, they genuinely are on screen, and the whole
   * page reveals itself before anyone scrolls. Waiting for the document to grow
   * did not fix it either: the document can be tall because of other content
   * while the grid is still collapsed.
   *
   * An observer does not have that problem. It reports when an element crosses
   * the viewport for real, whatever the layout does on the way there, and it
   * re-evaluates itself when things move.
   *
   * The original note here said to avoid IntersectionObserver because it can
   * silently never fire in some embedded webviews, and that is why the geometry
   * version was written. That caution was worth more than it cost: every
   * webview this store sees has supported IO for years, and the scroll fallback
   * below still covers anything that does not. What it was actually protecting
   * against - content stuck invisible - is handled by the failsafe regardless.
   */
  const reveal = (el) => {
    if (el.dataset.domRevealed === '1') return;
    el.dataset.domRevealed = '1';
    el.classList.add('dom-reveal-in');
  };

  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          obs.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px' })
    : null;

  // Fallback for anything without an observer: the original geometry check,
  // driven by scroll only, so it never runs during the unstable load window.
  const paint = () => {
    const vh = window.innerHeight;
    nodes.forEach((el) => {
      if (el.dataset.domRevealed === '1') return;
      const r = el.getBoundingClientRect();
      if (r.top < vh * 0.92 && r.bottom > 0) reveal(el);
    });
  };

  const observe = () => {
    nodes.forEach((el) => {
      if (el.dataset.domObserved === '1') return;
      el.dataset.domObserved = '1';
      if (io) io.observe(el);
    });
  };

  const refresh = () => { tag(); collect(); observe(); if (!io) paint(); };

  refresh();
})();
